import { useQuery } from '@tanstack/react-query';
import { Loader2, Plus, PowerOff, Sparkles } from 'lucide-react';
import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { ChatBody } from '@/components/assistant/AssistantChat';
import { useAssistantStream } from '@/components/assistant/useAssistantStream';

const SUGGESTIONS = [
  'What open tickets does Cora have?',
  'Which Datto RMM devices at Gradi Group are offline?',
  'Find the IT Glue documentation for printer setup at Cora',
  'Summarise ticket T20261012.0028',
] as const;

interface AgentStatus { enabled: boolean; available: boolean; reason: string | null }

/**
 * Full-screen chat with the Foundry agent (server/agent.ts → /api/assistant/agent/stream). Shown
 * only when the admin switch (`ai.chatEnabled`) is on AND the server's connection check with the
 * managed identity passes (GET …/status); otherwise the page says which. The agent looks things up itself through the Synfo MCP gateway; the conversation is kept in
 * sessionStorage so following a ticket link in a reply and coming back doesn't lose it.
 */
export default function AiChatPage() {
  const status = useQuery({
    queryKey: ['ai-agent-status'],
    queryFn: () => api.get<AgentStatus>('/api/assistant/agent/status'),
    staleTime: 60_000,
  });
  const ready = !!status.data?.enabled && !!status.data.available;
  const chat = useAssistantStream({
    endpoint: '/api/assistant/agent/stream',
    body: () => ({}),
    storageKey: 'cosmos:ai:chat',
  });

  return (
    <div className="mx-auto flex h-[calc(100dvh-7.5rem)] w-full max-w-4xl flex-col px-4 py-5 md:h-[calc(100dvh-3.5rem)] md:px-6">
      <PageHeader
        title="AI Assistant"
        subtitle={<>Looks things up in Autotask, Datto RMM and IT Glue · read-only · check answers before acting on them</>}
        actions={
          ready && <Button variant="outline" size="sm" onClick={chat.reset} disabled={chat.streaming || chat.messages.length === 0}>
            <Plus className="mr-1 h-4 w-4" /> New chat
          </Button>
        }
      />

      <div className="mt-4 flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card p-3 shadow-sm md:p-4">
        {status.isLoading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking the connection to the AI agent…
          </div>
        ) : !ready ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            {status.data?.enabled === false ? <PowerOff className="h-6 w-6" /> : <Sparkles className="h-6 w-6 text-primary" />}
            <p className="max-w-md font-medium text-foreground">
              {status.data?.enabled === false ? 'AI chat is turned off.' : 'AI chat is unavailable right now.'}
            </p>
            <p className="max-w-md">
              {status.data?.enabled === false
                ? 'An administrator can turn it back on in Settings → AI.'
                : (status.data?.reason ?? (status.error instanceof Error ? status.error.message : 'The connection check failed.'))}
            </p>
          </div>
        ) : (
          <ChatBody
            chat={chat}
            suggestions={SUGGESTIONS}
            suggestionsLabel="Ask about any client's tickets, devices or documentation. For example:"
            placeholder="Ask a question… (Enter to send, Shift+Enter for a new line)"
            scrollClass="min-h-0 flex-1 pr-1"
            bubbleClass="px-3.5 py-2.5 text-[14.5px] leading-relaxed"
          />
        )}
      </div>
    </div>
  );
}
